import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageTitle } from "@/hooks/use-page-title";
import { Label } from "@/components/ui/label";
import { BrandMark } from "@/components/brand-mark";
import { ExternalSignInButtons } from "@/components/auth/external-sign-in";
import { authApi } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { takePostLoginReturnTo } from "@/lib/post-login-return-to";
import { motion, useInView } from "framer-motion";

/* ------------------------------------------------------------------ */
/*  Scroll-triggered fade-up wrapper                                  */
/* ------------------------------------------------------------------ */
function FadeUp({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Right-panel copy — limited to the six product capabilities        */
/* ------------------------------------------------------------------ */
const HEADING_LINE_1 = 'Everything between "I have';
const HEADING_LINE_2 = 'an idea" and "it\'s shipped"';
const SUB_LINE_1 =
  "Stop stitching together a planning doc, a ticket tracker, and a pile of AI prompts.";
const SUB_LINE_2 =
  "CompBox is the whole path — captured, structured, and executable.";

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className: "w-5 h-5",
};

const FEATURES = [
  {
    title: "AI planning pipeline",
    desc: "A 5-stage pipeline — analyze, decompose, detail, evaluate, verify — turns a sentence into an epic with features, tasks, acceptance criteria, and execution order.",
    icon: (
      <svg {...iconProps}>
        <path d="M12 3v18M5 8l7-5 7 5M5 8v8l7 5 7-5V8" />
      </svg>
    ),
  },
  {
    title: "Multi-agent orchestration",
    desc: "The orchestrator delegates each feature to specialist agents — worker, reviewer, QA, security, architect — running in parallel where it's safe to.",
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="5" r="2.4" />
        <circle cx="5" cy="19" r="2.4" />
        <circle cx="19" cy="19" r="2.4" />
        <path d="M12 7.4v4M12 11.4 6.5 17M12 11.4 17.5 17" />
      </svg>
    ),
  },
  {
    title: "Compliance scoring",
    desc: "Every epic and request is graded against a 28-rule rubric — 0–100, with a pass / advisory / critical tier — so nothing enters the build half-specified.",
    icon: (
      <svg {...iconProps}>
        <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    title: "Session continuity",
    desc: "Decisions, progress, and handoff notes persist across sessions. The next agent — or the next you — picks up exactly where the last one stopped.",
    icon: (
      <svg {...iconProps}>
        <path d="M3 12a9 9 0 1 0 9-9" />
        <path d="M3 4v5h5" />
        <path d="M12 8v4l3 2" />
      </svg>
    ),
  },
  {
    title: "Send to my machine",
    desc: "Kick off a plan or an orchestrated build from the web — even from your phone — and it runs on your paired machine. No terminal required.",
    icon: (
      <svg {...iconProps}>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M2 20h20" />
      </svg>
    ),
  },
  {
    title: "MCP-native",
    desc: "A first-class MCP server exposes every capability to your AI tools, so agents create epics, log decisions, and run validations directly.",
    icon: (
      <svg {...iconProps}>
        <path d="M7 8l-4 4 4 4M17 8l4 4-4 4M14 4l-4 16" />
      </svg>
    ),
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export function LoginPage() {
  usePageTitle("Login");
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendNote, setResendNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSignInFloat, setShowSignInFloat] = useState(false);
  const [mobileGlowY, setMobileGlowY] = useState(50);
  const loginSectionRef = useRef<HTMLDivElement>(null);
  const landingSectionRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setUnverifiedEmail(null);
    setResendNote("");
    setIsLoading(true);

    const doLogin = async () => {
      try {
        await login(email, password);
        void navigate(takePostLoginReturnTo() ?? "/dashboard");
      } catch (err) {
        // A self-serve account that has not confirmed its inbox yet.
        const code =
          err instanceof ApiError
            ? (err.data as { error?: { code?: string } } | undefined)?.error?.code
            : undefined;
        if (code === "EMAIL_NOT_VERIFIED") {
          setUnverifiedEmail(email);
          setError("Please verify your email address first — check your inbox for the link.");
        } else {
          setError("Invalid email or password");
        }
      } finally {
        setIsLoading(false);
      }
    };

    void doLogin();
  };

  // Mobile: observe when login section scrolls out of view to show floating sign-in
  useEffect(() => {
    if (!loginSectionRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry) setShowSignInFloat(!entry.isIntersecting); },
      { threshold: 0.15 }
    );
    obs.observe(loginSectionRef.current);
    return () => { obs.disconnect(); };
  }, []);

  // Mobile: track scroll position for orange glow orb via ref on root container
  useEffect(() => {
    const isMobileView = window.innerWidth < 768;
    if (!isMobileView) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handler = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 50;
      setMobileGlowY(pct);
    };
    container.addEventListener('scroll', handler, { passive: true });
    return () => { container.removeEventListener('scroll', handler); };
  }, []);

  const scrollToLanding = () => {
    landingSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToLogin = () => {
    loginSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div ref={scrollContainerRef} className="flex flex-col md:flex-row min-h-screen bg-background overflow-y-auto md:overflow-hidden">
      {/* Mobile: orange glow orb that follows scroll — behind all content */}
      <div
        className="fixed inset-0 pointer-events-none z-0 md:hidden"
        aria-hidden="true"
      >
        <div
          className="absolute left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, var(--primary) 0%, #E89150 30%, transparent 70%)",
            top: `${String(mobileGlowY)}%`,
            transition: "top 0.3s ease-out",
            willChange: "top",
            filter: "blur(140px)",
          }}
        />
      </div>

      {/* ============================================================ */}
      {/*  LEFT — Sign-in form (fixed on desktop, in-flow on mobile)   */}
      {/*  Unchanged.                                                  */}
      {/* ============================================================ */}
      <div
        ref={loginSectionRef}
        className="relative md:fixed md:inset-y-0 md:left-0 w-full md:w-[40%] flex flex-col min-h-screen md:border-r border-border bg-background z-10 md:overflow-y-auto"
      >
        <BrandMark className="px-6 pt-6" />

        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Welcome back
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Sign in to your account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="text-sm text-red-400" role="alert">{error}</div>
              )}
              {unverifiedEmail && (
                <button
                  type="button"
                  className="text-sm text-primary hover:underline"
                  onClick={() => {
                    authApi
                      .resendVerification(unverifiedEmail)
                      .then((res) => {
                        setResendNote(res.message);
                      })
                      .catch(() => {
                        setResendNote("Could not send the email. Please try again shortly.");
                      });
                  }}
                >
                  Resend verification email
                </button>
              )}
              {resendNote && <div className="text-xs text-muted-foreground">{resendNote}</div>}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-muted-foreground">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  required
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-muted-foreground">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  required
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
            </form>

            <div className="mt-6 space-y-6">
              <ExternalSignInButtons onError={setError} />
              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="text-primary hover:underline">
                  Sign up
                </Link>
              </p>
            </div>

            {/* Legal text */}
            <p className="mt-6 text-center text-xs text-muted-foreground">
              By continuing, you agree to the{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
            {/* Mobile: Discover CompBox — glowing orange, inside the form area */}
            <button
              onClick={scrollToLanding}
              className="md:hidden relative mt-6 flex flex-col items-center gap-1.5 mx-auto group"
            >
              {/* Orange glow behind */}
              <span className="absolute -inset-x-16 -inset-y-10 rounded-full bg-primary/25 blur-3xl animate-pulse pointer-events-none" />
              <span className="relative text-sm font-semibold tracking-wide text-primary group-hover:text-primary/70 transition-colors">
                Discover CompBox
              </span>
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--primary)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="relative"
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <path d="m6 9 6 6 6-6" />
              </motion.svg>
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 text-xs text-muted-foreground/50 hidden md:block">
          © 2026 CompBox. All rights reserved.
        </div>

        {/* Mobile: copyright below form */}
        <div className="md:hidden flex justify-center px-6 pb-6">
          <p className="text-[10px] text-muted-foreground/40">
            © 2026 CompBox. All rights reserved.
          </p>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  RIGHT — Product panel (limited to the six capabilities)     */}
      {/* ============================================================ */}
      <div
        ref={landingSectionRef}
        className="relative z-[1] w-full md:w-[60%] md:ml-[40%] md:min-h-screen bg-[#0a0a0b] flex items-center justify-center px-6 md:px-14 lg:px-20 py-16 md:py-12"
      >
        {/* subtle orange glow behind the heading */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[420px] opacity-40"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(600px 300px at 50% 0%, var(--primary) 0%, transparent 60%)",
          }}
        />

        <div className="relative w-full max-w-4xl">
          {/* ---- Heading block ---- */}
          <FadeUp>
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-5">
                One platform
              </p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-white">
                <span className="block">{HEADING_LINE_1}</span>
                <span className="block">{HEADING_LINE_2}</span>
              </h2>
              <p className="mt-6 text-[15px] text-white/40 leading-relaxed max-w-xl mx-auto">
                <span className="block">{SUB_LINE_1}</span>
                <span className="block">{SUB_LINE_2}</span>
              </p>
            </div>
          </FadeUp>

          {/* ---- Six capability cards ---- */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-white/[0.1] transition-colors duration-200">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    {f.icon}
                  </div>
                  <h3 className="mt-4 text-[15px] font-semibold text-white">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-[13px] text-white/40 leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile: floating Sign In button when scrolled past login */}
      {showSignInFloat && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="md:hidden fixed bottom-4 left-4 right-4 z-50"
        >
          <button
            onClick={scrollToLogin}
            className="w-full py-3.5 rounded-2xl bg-primary text-white font-semibold text-sm shadow-2xl shadow-primary/30 active:scale-[0.97] transition-transform"
          >
            Sign In
          </button>
        </motion.div>
      )}
    </div>
  );
}
